import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Star, Shield, Truck, Users, Clock, Award } from "lucide-react";
import siteSettings from "../data/site-settings.json";
import productsData from "../data/products.json";
import testimonialsData from "../data/testimonials.json";
import { HOME } from "../constants/testIds";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } }
};

// Counter hook
const useCountUp = (target, duration = 2000, trigger) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!trigger) return;
    let start = 0;
    const increment = target / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [trigger, target, duration]);
  return count;
};

// Hero Section
const HeroSection = () =>
<section
  className="relative min-h-screen bg-shadowWhite flex items-center pt-20"
  data-testid={HOME.heroSection}>

    {/* Background subtle pattern */}
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute -top-20 -right-20 w-96 h-96 bg-softIvory/60 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-80 h-80 bg-softIvory/40 rounded-full blur-3xl" />
    </div>

    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full py-16 lg:py-0">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left: Text */}
        <motion.div
        initial="hidden"
        animate="visible"
        variants={stagger}
        className="order-2 lg:order-1">

          <motion.span
          variants={fadeUp}
          className="section-label mb-6 block">

            Premium Food Supplier — Dubai, UAE
          </motion.span>

          <motion.h1
          variants={fadeUp}
          className="font-cormorant text-5xl sm:text-6xl lg:text-7xl font-bold text-deepCharcoal leading-tight tracking-tight mb-6"
          data-testid={HOME.heroHeadline}>

            {siteSettings.hero_headline}
          </motion.h1>

          <motion.div variants={fadeUp} className="gold-divider mb-6" />

          <motion.p
          variants={fadeUp}
          className="font-outfit text-base text-[#4A4A4A] leading-relaxed mb-8 max-w-lg">

            {siteSettings.hero_subheadline}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 mb-10">
            <Link
            to="/products"
            className="inline-flex items-center justify-center space-x-2 bg-premiumGold text-deepCharcoal px-7 py-3.5 font-outfit font-semibold text-sm hover:bg-[#B8944A] transition-colors duration-200"
            data-testid={HOME.heroCtaProducts}>

              <span>View Products</span>
              <ArrowRight size={16} />
            </Link>
            <a
            href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
              "Hello AL ZAHRAN FLOUR MILL, I would like to place an order."
            )}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center space-x-2 border border-deepCharcoal text-deepCharcoal px-7 py-3.5 font-outfit font-semibold text-sm hover:bg-deepCharcoal hover:text-white transition-all duration-200"
            data-testid={HOME.heroCtaWhatsapp}>

              <span>Order on WhatsApp</span>
            </a>
          </motion.div>

          {/* Contact Strip */}
          <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-goldBorder">

            <a href={`tel:${siteSettings.phone}`} className="flex items-center space-x-2 text-sm font-outfit text-[#4A4A4A] hover:text-premiumGold transition-colors">
              <span className="w-1.5 h-1.5 bg-premiumGold rounded-full flex-shrink-0" />
              <span>{siteSettings.phone}</span>
            </a>
            <a href={`mailto:${siteSettings.email}`} className="flex items-center space-x-2 text-sm font-outfit text-[#4A4A4A] hover:text-premiumGold transition-colors">
              <span className="w-1.5 h-1.5 bg-premiumGold rounded-full flex-shrink-0" />
              <span>{siteSettings.email}</span>
            </a>
            <span className="flex items-center space-x-2 text-sm font-outfit text-[#4A4A4A]">
              <span className="w-1.5 h-1.5 bg-[#25D366] rounded-full flex-shrink-0" />
              <span>{siteSettings.hours}</span>
            </span>
          </motion.div>
        </motion.div>

        {/* Right: Images */}
        <motion.div
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.8, ease: "easeOut", delay: 0.3 }}
        className="order-1 lg:order-2 relative">

          {/* Main logo display */}
          <div className="relative bg-softIvory border border-goldBorder p-8 mb-4">
            <div className="flex items-center justify-center mb-6">
              <img
              src={siteSettings.logo}
              alt="AL ZAHRAN FLOUR MILL Logo"
              className="w-40 h-40 object-contain" />

            </div>
            <div className="text-center">
              <p className="font-cormorant text-2xl font-bold text-deepCharcoal tracking-wide">AL ZAHRAN MILL

            </p>
              <p className="section-label mt-1">HORALANZ</p>
            </div>
            {/* Gold corner accents */}
            <span className="absolute top-3 left-3 w-6 h-6 border-t-2 border-l-2 border-premiumGold" />
            <span className="absolute top-3 right-3 w-6 h-6 border-t-2 border-r-2 border-premiumGold" />
            <span className="absolute bottom-3 left-3 w-6 h-6 border-b-2 border-l-2 border-premiumGold" />
            <span className="absolute bottom-3 right-3 w-6 h-6 border-b-2 border-r-2 border-premiumGold" />
          </div>

          {/* Store sign image */}
          <div className="relative overflow-hidden">
            <img
            src={siteSettings.store_image}
            alt="AL ZAHRAN FLOUR MILL Store"
            className="w-full h-52 object-cover" />

            <div className="absolute inset-0 bg-gradient-to-t from-deepCharcoal/50 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <p className="font-outfit text-xs text-white/90 tracking-widest uppercase">
                Serving Dubai Since Establishment
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>;


// Overview Section
const OverviewSection = () => {
  const highlights = [
  { icon: <CheckCircle size={22} className="text-premiumGold" />, title: "Quality Products", text: "Carefully sourced from trusted suppliers with strict quality standards." },
  { icon: <Truck size={22} className="text-premiumGold" />, title: "Reliable Sourcing", text: "Consistent supply chain ensuring product availability year-round." },
  { icon: <Star size={22} className="text-premiumGold" />, title: "Competitive Pricing", text: "Best market rates for both wholesale bulk and retail purchases." },
  { icon: <Users size={22} className="text-premiumGold" />, title: "Professional Service", text: "Dedicated team committed to customer satisfaction at every step." }];


  return (
    <section className="bg-softIvory py-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}>

          {highlights.map((item, i) =>
          <motion.div
            key={i}
            variants={fadeUp}
            className="flex flex-col items-start p-6 bg-white border border-goldBorder hover:-translate-y-1 transition-transform duration-300">

              <div className="mb-4">{item.icon}</div>
              <h3 className="font-cormorant text-xl font-semibold text-deepCharcoal mb-2">
                {item.title}
              </h3>
              <p className="font-outfit text-sm text-[#4A4A4A] leading-relaxed">{item.text}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Categories Section
const CategoriesSection = () =>
<section className="py-24 bg-shadowWhite" data-testid={HOME.categoriesSection}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
      className="text-center mb-14"
      initial="hidden"
      whileInView="visible"
      variants={stagger}
      viewport={{ once: true }}>

        <motion.span variants={fadeUp} className="section-label">
          What We Offer
        </motion.span>
        <motion.h2
        variants={fadeUp}
        className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4">

          Featured Product Categories
        </motion.h2>
        <motion.div variants={fadeUp} className="gold-divider mx-auto" />
      </motion.div>

      <motion.div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
      variants={stagger}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}>

        {productsData.categories.map((cat) =>
      <motion.div
        key={cat.id}
        variants={fadeUp}
        className="category-card bg-white border border-goldBorder overflow-hidden group hover:-translate-y-1 transition-transform duration-300"
        data-testid={HOME.categoryCard}>

            <div className="overflow-hidden h-52">
              <img
            src={cat.image}
            alt={cat.name}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy" />

            </div>
            <div className="p-6">
              <h3 className="font-cormorant text-2xl font-bold text-deepCharcoal mb-2">
                {cat.name}
              </h3>
              <p className="font-outfit text-sm text-[#4A4A4A] leading-relaxed mb-4">
                {cat.description}
              </p>
              <Link
            to={`/products/${cat.slug}`}
            className="inline-flex items-center space-x-1 text-premiumGold font-outfit text-sm font-semibold hover:text-[#B8944A] transition-colors group/link">

                <span>Explore</span>
                <ArrowRight size={14} className="group-hover/link:translate-x-1 transition-transform" />
              </Link>
            </div>
          </motion.div>
      )}
      </motion.div>

      <motion.div
      className="text-center mt-10"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: 0.3 }}>

        <Link
        to="/products"
        className="inline-flex items-center space-x-2 border border-premiumGold text-premiumGold px-8 py-3 font-outfit font-semibold text-sm hover:bg-premiumGold hover:text-deepCharcoal transition-all duration-200">

          <span>View All Products</span>
          <ArrowRight size={15} />
        </Link>
      </motion.div>
    </div>
  </section>;


// Why Choose Us
const WhyChooseUsSection = () => {
  const features = [
  { icon: <Award size={26} className="text-premiumGold" />, title: "Premium Quality Products", text: "Every product is carefully selected from certified suppliers meeting the highest quality standards." },
  { icon: <Shield size={26} className="text-premiumGold" />, title: "Trusted Supply Network", text: "Established relationships with reliable manufacturers and importers across the region." },
  { icon: <Truck size={26} className="text-premiumGold" />, title: "Wholesale & Retail Supply", text: "Flexible purchasing options for both bulk wholesale orders and individual retail needs." },
  { icon: <Star size={26} className="text-premiumGold" />, title: "Competitive Pricing", text: "Best-in-market pricing with transparent quotes and no hidden charges." },
  { icon: <Clock size={26} className="text-premiumGold" />, title: "Fast Customer Support", text: "Responsive WhatsApp-based support for quick inquiries and order processing." },
  { icon: <CheckCircle size={26} className="text-premiumGold" />, title: "Reliable Service", text: "Consistent stock availability and professional service every day of the week." }];


  return (
    <section className="py-24 bg-softIvory" data-testid={HOME.whySection}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-14"
          initial="hidden"
          whileInView="visible"
          variants={stagger}
          viewport={{ once: true }}>

          <motion.span variants={fadeUp} className="section-label">
            Our Advantages
          </motion.span>
          <motion.h2
            variants={fadeUp}
            className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4">

            Why Choose AL ZAHRAN?
          </motion.h2>
          <motion.div variants={fadeUp} className="gold-divider mx-auto" />
        </motion.div>

        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={stagger}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}>

          {features.map((feat, i) =>
          <motion.div
            key={i}
            variants={fadeUp}
            className="bg-white p-8 border border-goldBorder hover:-translate-y-1 transition-transform duration-300">

              <div className="mb-4">{feat.icon}</div>
              <h3 className="font-cormorant text-xl font-semibold text-deepCharcoal mb-3">
                {feat.title}
              </h3>
              <p className="font-outfit text-sm text-[#4A4A4A] leading-relaxed">{feat.text}</p>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>);

};

// Stats Section
const StatCard = ({ number, suffix, label }) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  const count = useCountUp(number, 2000, inView);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setInView(true);},
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="text-center px-6 py-8">
      <p className="font-cormorant text-6xl font-bold text-premiumGold leading-none mb-2">
        {count}{suffix}
      </p>
      <p className="section-label" style={{ color: "rgba(255,255,255,0.7)" }}>{label}</p>
    </div>);

};

const StatsSection = () =>
<section className="bg-deepCharcoal py-20" data-testid={HOME.statsSection}>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
      className="text-center mb-10"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}>

        <p className="font-cormorant text-3xl sm:text-4xl font-semibold text-white">
          Our Business At A Glance
        </p>
      </motion.div>
      <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-white/10">
        <StatCard number={6} suffix="+" label="Product Categories" />
        <StatCard number={3} suffix="" label="Business Partners" />
        <StatCard number={365} suffix="" label="Days of Operation" />
        <StatCard number={2} suffix="" label="Wholesale & Retail Services" />
      </div>
    </div>
  </section>;


// Testimonials
const TestimonialsSection = () =>
<section className="py-24 bg-shadowWhite">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div
      className="text-center mb-14"
      initial="hidden"
      whileInView="visible"
      variants={stagger}
      viewport={{ once: true }}>

        <motion.span variants={fadeUp} className="section-label">Customer Reviews</motion.span>
        <motion.h2
        variants={fadeUp}
        className="font-cormorant text-4xl sm:text-5xl font-bold text-deepCharcoal mt-3 mb-4">

          What Our Customers Say
        </motion.h2>
        <motion.div variants={fadeUp} className="gold-divider mx-auto" />
      </motion.div>

      <motion.div
      className="grid grid-cols-1 md:grid-cols-3 gap-6"
      variants={stagger}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}>

        {testimonialsData.testimonials.map((t, i) =>
      <motion.div
        key={i}
        variants={fadeUp}
        className="bg-white border border-goldBorder p-8 relative">

            <div className="flex mb-4">
              {[...Array(t.rating)].map((_, s) =>
          <Star key={s} size={14} className="text-premiumGold fill-premiumGold mr-0.5" />
          )}
            </div>
            <p className="font-outfit text-sm text-[#4A4A4A] leading-relaxed mb-6 italic">
              "{t.text}"
            </p>
            <div className="border-t border-goldBorder pt-4">
              <p className="font-cormorant text-lg font-semibold text-deepCharcoal">{t.name}</p>
              <p className="font-outfit text-xs text-premiumGold">{t.company}</p>
            </div>
            {/* Gold quote mark */}
            <span className="absolute top-6 right-6 font-cormorant text-5xl text-premiumGold/20 leading-none select-none">
              "
            </span>
          </motion.div>
      )}
      </motion.div>
    </div>
  </section>;


// CTA Banner
const CTASection = () =>
<section
  className="relative py-24 overflow-hidden"
  style={{ background: "linear-gradient(135deg, #1A1A1A 0%, #2D2416 100%)" }}
  data-testid={HOME.ctaBanner}>

    <div className="absolute inset-0 opacity-10">
      <img
      src={siteSettings.store_interior}
      alt=""
      className="w-full h-full object-cover" />

    </div>
    <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <motion.div
      initial="hidden"
      whileInView="visible"
      variants={stagger}
      viewport={{ once: true }}>

        <motion.span variants={fadeUp} className="section-label">
          Ready to Order?
        </motion.span>
        <motion.h2
        variants={fadeUp}
        className="font-cormorant text-4xl sm:text-5xl font-bold text-white mt-3 mb-4">

          Get in Touch for Bulk Orders & Inquiries
        </motion.h2>
        <motion.div variants={fadeUp} className="gold-divider mx-auto mb-6" />
        <motion.p
        variants={fadeUp}
        className="font-outfit text-base text-white/70 mb-8 leading-relaxed">

          Contact us directly on WhatsApp for immediate pricing, availability,
          and delivery arrangements. We're open daily from 8 AM to midnight.
        </motion.p>
        <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center">
          <a
          href={`https://wa.me/${siteSettings.whatsapp}?text=${encodeURIComponent(
            "Hello AL ZAHRAN FLOUR MILL, I would like to place an order."
          )}`}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center space-x-2 bg-premiumGold text-deepCharcoal px-8 py-3.5 font-outfit font-semibold text-sm hover:bg-[#B8944A] transition-colors duration-200">

            <span>Order on WhatsApp</span>
          </a>
          <Link
          to="/contact"
          className="inline-flex items-center justify-center space-x-2 border border-white/40 text-white px-8 py-3.5 font-outfit font-semibold text-sm hover:border-premiumGold hover:text-premiumGold transition-all duration-200">

            <span>Contact Us</span>
          </Link>
        </motion.div>
      </motion.div>
    </div>
  </section>;


const HomePage = () =>
<>
    <HeroSection />
    <OverviewSection />
    <CategoriesSection />
    <WhyChooseUsSection />
    <StatsSection />
    <TestimonialsSection />
    <CTASection />
  </>;


export default HomePage;